import { useEffect, useRef, useState } from "react";
import { Button, Col, Drawer, Input, Modal, Row, Select } from "antd";
import dayjs from "dayjs";
import {
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useForm } from "antd/lib/form/Form";

import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import { showNotification } from "../../../shared/utils/utils";

import {
  createEmailConfiguration,
  updateEmailConfiguration,
  sendTestEmail,
} from "../../../core/store/reducers/email-configuration.slice";
import { Client } from "../../../core/api-client";
import { API_GET_STAFF_LIST } from "../../../shared/routes/private.routes";
import { message } from "antd/lib";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;

}

const EMAIL_TRIGGER_OPTIONS = [
  {
    value: "project-draft-approval",
    label: "Project Draft Approval",
    description:
      "Triggers 1 workflow email. Email type: project approval request. This is sent when a new project is created and saved in Draft status.",
  },
  {
    value: "project-approval-completed",
    label: "Project Approval Completed",
    description:
      "Triggers 1 workflow email. Email type: project approval completed. This is sent when a Draft project is approved and moved to Active status.",
  },
  {
    value: "staff-contract-expiry",
    label: "Staff Contract Expiry",
    description:
      "Triggers 3 scheduled email types. Email types: expired staff contracts, upcoming staff contracts, and staff notice period alerts.",
  },
  {
    value: "staff-draft-approval",
    label: "Staff Draft Approval",
    description:
      "Triggers 1 workflow email. Email type: staff approval request. This is sent when a new staff profile is created and saved in Draft status.",
  },
  {
    value: "staff-approval-completed",
    label: "Staff Approval Completed",
    description:
      "Triggers 1 workflow email. Email type: staff approval completed. This is sent when a Draft staff profile is approved and moved to Active status.",
  },
  {
    value: "trainee-completion",
    label: "Trainee Completion",
    description:
      "Triggers 2 scheduled email types. Email types: trainees completed 180 days and trainees approaching 180 days.",
  },
  {
    value: "project-sow-expiry",
    label: "Project/SOW Expiry",
    description:
      "Triggers 2 scheduled email types. Email types: expired Project/SOW records and upcoming Project/SOW renewal or deactivation alerts.",
  },
  {
    value: "milestone-invoice-weekly",
    label: "Milestone Invoice Weekly",
    description:
      "Triggers 2 scheduled email types. Email types: Raised overdue milestone invoices and Not Due milestones within the next 30 days.",
  },
];

const EMAIL_TRIGGER_DESCRIPTIONS = EMAIL_TRIGGER_OPTIONS.reduce(
  (descriptions, option) => ({
    ...descriptions,
    [option.value]: option.description,
  }),
  {} as Record<string, string>
);

const CreateEditEmailConfigurationComponent = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const drawerBodyRef = useRef<HTMLDivElement | null>(null);

  const [recipients, setRecipients] = useState<string[]>([""]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [showRecipients, setShowRecipients] = useState(true);

  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [isTestEmailLoading, setIsTestEmailLoading] = useState(false);

  const [otherEmails, setOtherEmails] = useState<string[]>([]);

  const [showOtherInput, setShowOtherInput] = useState(false);

  const [otherEmail, setOtherEmail] = useState("");

  const [scheduleType, setScheduleType] = useState("DAILY");
  const [recipientDropdownOpen, setRecipientDropdownOpen] =
    useState(false);
  const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);
  /**
   * Add new recipient
   */
  const addRecipient = () => {
    setRecipients([...recipients, ""]);
  };

  /**
   * Remove recipient
   */
  const removeRecipient = (index: number) => {
    if (recipients.length === 1) return;

    setRecipients(
      recipients.filter((_, recipientIndex) => recipientIndex !== index)
    );
  };

  const staffEmailOptions = Array.from(
    new Map(
      staffList
        .filter((staff) => staff?.email)
        .map((staff) => [
          staff.email.toLowerCase(),
          {
            value: staff.email,
            label: (
              <div>
                <div style={{ fontWeight: 500 }}>
                  {staff.staffName}
                </div>
                <div style={{ fontSize: 12, color: "#676373" }}>
                  {staff.email}
                </div>
              </div>
            ),
            searchText: `${staff.staffName} ${staff.email}`.toLowerCase(),
          },
        ])
    ).values()
  );
  const recipientOptions = [
    ...staffEmailOptions,
    // {
    //   value: "OTHER",
    //   label: "Other",
    // },
  ];

  // const getRecipientOptions = (currentIndex: number) => {
  //   const selectedEmails = recipients.filter(
  //     (_, index) => index !== currentIndex
  //   );

  //   return staffEmailOptions.filter(
  //     (option) => !selectedEmails.includes(option.value)
  //   );
  // };


  const getRecipientOptions = (currentIndex: number) => {
    const selectedEmails = recipients.filter(
      (_, index) => index !== currentIndex
    );

    return staffEmailOptions
      .filter((option) => !selectedEmails.includes(option.value))
      .map((option) => ({
        ...option,
        label: (
          <div>
            <div style={{ fontWeight: 500 }}>
              {option.label}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#676373",
              }}
            >
              {option.value}
            </div>
          </div>
        ),
      }));
  };

  const fetchStaffList = async () => {
    const response = await Client.post(
      API_GET_STAFF_LIST,
      { staffStatusId: ["ACTIVE",] },
      { params: { page: 1, limit: 10000 } }
    );
    if (response?.data?.data) {
      setStaffList(response.data.data);
    } else {
      setStaffList([]);
    }
  };
  /**
   * Update recipient email
   */
  const updateRecipient = (index: number, value: string) => {
    const updatedRecipients = [...recipients];
    updatedRecipients[index] = value;
    setRecipients(updatedRecipients);
  };

  /**
   * Set form values for Edit
   */
  useEffect(() => {
    fetchStaffList();
    if (selectedEntity) {
      setScheduleType(selectedEntity.scheduleType || "DAILY");
      form.setFieldsValue({
        notificationKey: selectedEntity?.notificationKey,
        subject: selectedEntity?.subject,
        description: selectedEntity?.description,
        status: selectedEntity?.status ? "ACTIVE" : "INACTIVE",
        scheduleType: selectedEntity?.scheduleType,
        scheduleDay: selectedEntity?.scheduleDay,
        scheduleTime: selectedEntity?.scheduleTime
          ? dayjs(selectedEntity.scheduleTime.trim(), "hh:mm A")
          : null,
      });
      const existingEmails =
        selectedEntity?.recipients
          ?.filter((recipient: any) => recipient?.isActive)
          ?.map((recipient: any) => recipient.emailId) || [];

      setSelectedEmails(existingEmails);

      // If you want to show Other separately
      setOtherEmail("");
      setShowOtherInput(false);
    } else {
      form.resetFields();
      setScheduleType("DAILY");
      form.setFieldsValue({
        status: "ACTIVE",
      });

      setRecipients([""]);
    }
  }, [selectedEntity, form]);

  /**
   * Submit
   */
  const onFinish = (values: any) => {
    const allRecipients = [
      ...selectedEmails,
      ...(showOtherInput && otherEmail.trim()
        ? [otherEmail.trim()]
        : []),
    ];

    const validRecipients = allRecipients
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email !== "");

    if (validRecipients.length === 0) {
      showNotification(
        "Please add at least one recipient email",
        "error"
      );
      return;
    }

    const invalidRecipient = validRecipients.find(
      (email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    );

    if (invalidRecipient) {
      showNotification(
        "Please enter a valid recipient email",
        "error"
      );
      return;
    }
    const generateNotificationKey = (subject: string): string => {
      return subject
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-");
    };

    const payload = {
      notificationKey: generateNotificationKey(values.subject),
      subject: values.subject,
      description: values.description,
      isActive: values.status === "ACTIVE",
      // Scheduler configuration
      scheduleType: values.scheduleType || "DAILY",
      scheduleDay:
        values.scheduleType === "WEEKLY"
          ? values.scheduleDay
          : null,
      scheduleTime: values.scheduleTime
        ? values.scheduleTime.format("hh:mm A")
        : null,
      recipients: validRecipients.map((email) => ({
        emailId: email,
        isActive: true,
      })),
    };
    console.log("PAYLOAD:", payload);

    console.log("Email Configuration Payload:", payload);

    if (selectedEntity) {
      (dispatch(
        updateEmailConfiguration({
          id: selectedEntity?.id,
          body: payload,
        })
      ) as any).then((res: any) => {
        console.log("update", res)
        if (res?.payload?.success) {
          // showNotification(
          //   "Email Configuration Updated Successfully",
          //   "success"
          // );
          setShowTestEmailModal(true);


          return;
        }
        else {
          const statusCode = res?.payload?.statusCode;

          if (statusCode === 400) {
            Modal.warning({
              title: "Notification Not Configured",
              content:
                "The email template for " + values.subject + " has not been configured yet. Please contact the IT/Development team to configure the template before activating this notification.",
            });
            onSubmit();
            return;
          }

          Modal.warning({
            title: "Update Failed",
            content:
              res?.payload?.message ||
              "Unable to update email configuration. Please try again.",
          });
          
        }
      });
    } else {
      (dispatch(createEmailConfiguration(payload)) as any).then(
        (res: any) => {
          if (res?.meta?.requestStatus === "fulfilled") {
            // showNotification(
            //     "New Email Configuration Created Successfully",
            //     "success"
            // );  
            setShowSubmitSuccessModal(true);

          }

        }
      );
    }
  };

  /**
   * Form validation failed
   */
  const onFinishFailed = (_errorInfo: any) => {
    return;
  };

  const handleSendTestEmail = async () => {
    if (!selectedEntity?.id || !selectedEntity?.notificationKey) {
      return;
    }

    setIsTestEmailLoading(true);

    try {
      (dispatch(
        sendTestEmail({
          id: selectedEntity?.id,
          notificationKey: selectedEntity.notificationKey,
        })
      ) as any).then((res: any) => {
        console.log("update", res)
        if (res?.payload?.success) {

          showNotification(
            res?.message || "Test email sent successfully.",
            "success"
          );

          setShowTestEmailModal(false);
          setIsTestEmailLoading(false);
          onSubmit();
        } else {
          const statusCode = res?.payload?.statusCode;

          if (statusCode === 400) {
            Modal.warning({
              title: "Notification Not Configured",
              content:
                "The email template for " + selectedEntity.subject + " has not been configured yet. Please contact the IT/Development team to configure the template before activating this notification.",
            });
            onSubmit();
            return;
          }

          Modal.warning({
            title: "Test Email Not Available",
            content:
              res?.payload?.message ||
              "Unable to update email configuration. Please try again.",
          });
          setIsTestEmailLoading(false);
          setShowTestEmailModal(false);
          onSubmit();
        }
      });
    }
    catch (error: any) {
      Modal.error({
        title: "Test Email Failed",
        content:
          error?.message || "Unable to send test email.",
      });
    }
  };
  /**
   * Form value change
   */
  const onValuesChange = (
    changedValues: any,
    _allValues: any
  ) => {
    if (changedValues.notificationKey) {
      form.setFieldValue(
        "description",
        EMAIL_TRIGGER_DESCRIPTIONS[changedValues.notificationKey] || ""
      );
    }

    if (changedValues.scheduleType) {
      setScheduleType(changedValues.scheduleType);

      if (changedValues.scheduleType === "DAILY") {
        form.setFieldValue("scheduleDay", undefined);
      }
    }
  };

  const formProps = {
    form: form,

    name: "create-edit-email-configuration",

    heading: selectedEntity
      ? "Edit Email Configuration"
      : "Add New Email Configuration",

    initialValues: {
      status: "ACTIVE",
    },

    formElements: [
      ...(selectedEntity
        ? [
          {
            name: "notificationKey",
            colSpan: 12,
            label: "Email Notification",
            type: "Input",
            disabled: !!selectedEntity,
            allowClear: true,

            // rules: [
            //   {
            //     required: true,
            //     message: "Please enter Notification Key",
            //   },
            // ],
          },
        ]
        : []),
      {
        name: "subject",
        colSpan: 12,
        label: "Subject",
        placeholder: "Enter Subject",
        type: "Input",
        allowClear: true,
        rules: [
          {
            required: true,
            whitespace: true,
            message: "Please enter a valid subject",
          },
        ],
      },

      {
        name: "description",
        colSpan: 24,
        label: "Description",
        placeholder: "Enter a brief description for this notification",
        type: "TextArea",
        rows: 3,
        allowClear: true,
      },
      ...(selectedEntity
        ? [
          {
            name: "status",
            label: "Status",
            placeholder: "Select Status",
            type: "Select",
            colSpan: 12,
            allowClear: true,
            options: [
              {
                value: "ACTIVE",
                label: "ACTIVE",
              },
              {
                value: "INACTIVE",
                label: "INACTIVE",
              },
            ],
            rules: [
              {
                required: true,
                message: "Please select Status",
              },
            ],
          },



        ]
        : []),

      // Schedule Type    
      ...(selectedEntity && selectedEntity.status
        ? [
          {
            name: "scheduleType",
            label: "Schedule",
            placeholder: "Select Schedule",
            type: "Select",
            colSpan: 12,
            allowClear: false,
            options: [
              {
                value: "DAILY",
                label: "Every Day",
              },
              {
                value: "WEEKLY",
                label: "Weekly Once",
              },
              {
                value: "ON_TRIGGER",
                label: "On Trigger",
              },
            ],
            rules: [
              {
                required: true,
                message: "Please select Schedule",
              },
            ],
          },
        ]
        : []),
      // Weekly Day
      ...(scheduleType === "WEEKLY"
        ? [
          {
            name: "scheduleDay",
            label: "Day",
            placeholder: "Select Day",
            type: "Select",
            colSpan: 12,
            allowClear: false,
            options: [
              {
                value: "MONDAY",
                label: "Monday",
              },
              {
                value: "TUESDAY",
                label: "Tuesday",
              },
              {
                value: "WEDNESDAY",
                label: "Wednesday",
              },
              {
                value: "THURSDAY",
                label: "Thursday",
              },
              {
                value: "FRIDAY",
                label: "Friday",
              },
              {
                value: "SATURDAY",
                label: "Saturday",
              },
              {
                value: "SUNDAY",
                label: "Sunday",
              },
            ],
            rules: [
              {
                required: true,
                message: "Please select Day",
              },
            ],
          },
        ]
        : []),
      ...(scheduleType !== "ON_TRIGGER" && selectedEntity && selectedEntity.status
        ? [
          // Time
          {
            name: "scheduleTime",
            label: "Time",
            placeholder: "Select Time",
            type: "TimePicker",
            colSpan: 12,
            format: "HH:mm A",
            use12Hours: true,

          },
        ]
        : [])
    ],

    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },

      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: () => {
          form.resetFields();
          setRecipients([""]);
          setSelectedEmails([]);
          setOtherEmail("");
          setShowOtherInput(false);
        },
      },

      {
        htmlType: "submit",
        colSpan: 5,
        loading: false,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],

    onFinish: onFinish,

    onFinishFailed: onFinishFailed,

    onValuesChange: onValuesChange,
  };



  return (
    <Drawer
      maskClosable={false}
      title={
        <strong>
          {formProps.heading}
        </strong>
      }
      className="form-drawer"
      width="720px"
      onClose={onClose}
      open={visible}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
        },
      }}
    >
      <div
        ref={drawerBodyRef}
        className="email-configuration-drawer-body"
        style={{ paddingBottom: 100 }}
      >
        <GenericFormComponent {...formProps} />

        {/* RECIPIENT SECTION */}
        {/* <div
        // style={{
        //   marginTop: 20,
        //   paddingTop: 10,
        // }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{
            marginBottom: 12,
          }}
        >
          <Col>
            <strong>Recipients</strong>
          </Col>

          <Col>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addRecipient}
            >
              Add Recipient
            </Button>
          </Col>
        </Row>

        {recipients.map((email, index) => (
          <Row
            key={index}
            gutter={12}
            align="middle"
            style={{
              marginBottom: 12,
            }}
          >
            <Col flex="1">
              <Input
                value={email}
                placeholder="Enter recipient email"
                onChange={(e) =>
                  updateRecipient(
                    index,
                    e.target.value
                  )
                }
              />
            </Col>

            <Col>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={recipients.length === 1}
                onClick={() =>
                  removeRecipient(index)
                }
              />
            </Col>
          </Row>
        ))}
      </div> */}

        {/* <div>
  <Row
    justify="space-between"
    align="middle"
    style={{
      marginBottom: 12,
    }}
  >
    <Col>
      <strong>Recipients</strong>
    </Col>

    <Col>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addRecipient}
      >
        Add Recipient
      </Button>
    </Col>
  </Row>

  {recipients.map((email, index) => (
    <Row
      key={index}
      gutter={12}
      align="middle"
      style={{
        marginBottom: 12,
      }}
    >
      <Col flex="1">
        <Select
          value={email || undefined}
          placeholder="Select recipient email"
          options={staffEmailOptions}
          showSearch
          allowClear
          style={{ width: "100%" }}
          optionFilterProp="label"
          onChange={(value) => {
            updateRecipient(index, value || "");
          }}
          status={!email ? "error" : ""}
        />
      </Col>

      <Col>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          disabled={recipients.length === 1}
          onClick={() => removeRecipient(index)}
        />
      </Col>
    </Row>
  ))}
</div> */}
        <div style={{ marginTop: -100 }}>
          {/* Add Recipient Button */}
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Col style={{ flexDirection: "row-reverse" }}>
              <strong
                style={{
                  textTransform: "uppercase",
                  lineHeight: "16px",
                  fontWeight: 600,
                  color: "#676373",
                }}
              >
                Recipients
              </strong>
            </Col>

            {/* <Col>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => setShowRecipients(true)}
      >
        Add Recipient
      </Button>
    </Col> */}
          </Row>

          {showRecipients && (
            <>
              {/* Multiple Staff Email Dropdown */}
              {/* <Select
        mode="multiple"
        showSearch
        allowClear
        getPopupContainer={() => drawerBodyRef.current || document.body}
        listHeight={160}
        dropdownStyle={{
          maxHeight: 180,
          overflowY: "auto",
        }}
        style={{
          width: "100%",
          marginBottom: 16,
        }}
        placeholder="Search and select recipient emails"
        optionFilterProp="label"
        options={recipientOptions}
        value={selectedEmails}
        open={recipientDropdownOpen}
        onDropdownVisibleChange={setRecipientDropdownOpen}
        onChange={(values) => {
            setSelectedEmails(values);
          }}
           onInputKeyDown={(e) => {
          if (e.key !== "Enter") return;

          const input = (e.target as HTMLInputElement).value.trim();

          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

          if (!input) return;

          if (!emailRegex.test(input)) {
          e.preventDefault();

          message.error("Please enter a valid email address");

          return;
          }

          if (!selectedEmails.includes(input)) {
          setSelectedEmails((prev) => [...prev, input]);
          }

          e.preventDefault();
          setOtherEmail("");
          }}
      /> */}
              <Select
                mode="multiple"
                showSearch
                allowClear
                style={{
                  width: "100%",
                  marginBottom: 16,
                }}
                placeholder="Search and select recipient emails"
                optionFilterProp="label"
                options={recipientOptions}
                filterOption={(input, option) =>
                  (option?.searchText ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={selectedEmails}
                searchValue={searchValue}
                open={recipientDropdownOpen}
                onDropdownVisibleChange={(open) => {
                  setRecipientDropdownOpen(open);

                  if (!open) {
                    const email = searchValue.trim();

                    if (!email) return;

                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    if (!emailRegex.test(email)) {
                      message.error("Please enter a valid email address");
                      return;
                    }

                    if (!selectedEmails.includes(email)) {
                      setSelectedEmails((prev) => [...prev, email]);
                    }

                    setSearchValue("");
                  }
                }}
                onSearch={(value) => {
                  setSearchValue(value);
                }}
                onChange={(values) => {
                  setSelectedEmails(values);
                  setSearchValue("");
                }}
                onInputKeyDown={(e) => {
                  if (e.key !== "Enter") return;

                  const email = searchValue.trim();

                  if (!email) return;

                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                  if (!emailRegex.test(email)) {
                    e.preventDefault();
                    message.error("Please enter a valid email address");
                    return;
                  }

                  e.preventDefault();

                  if (!selectedEmails.includes(email)) {
                    setSelectedEmails((prev) => [...prev, email]);
                  }

                  setSearchValue("");
                  setRecipientDropdownOpen(false);
                }}
              />

              {/* Selected Staff Emails */}
              {selectedEmails.map((email) => (
                <Row
                  key={email}
                  gutter={12}
                  align="middle"
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <Col flex="1">
                    <Input
                      value={email}
                      disabled
                    />
                  </Col>

                  <Col>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setSelectedEmails(
                          selectedEmails.filter(
                            (item) => item !== email
                          )
                        );
                      }}
                    />
                  </Col>
                </Row>
              ))}

              {/* Other Email */}
              {showOtherInput && (
                <Row
                  gutter={12}
                  align="middle"
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <Col flex="1">
                    <Input
                      value={otherEmail}
                      placeholder="Enter recipient email"
                      onChange={(e) => setOtherEmail(e.target.value)}
                      status={
                        otherEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otherEmail)
                          ? "error"
                          : ""
                      }
                    />

                    {otherEmail &&
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otherEmail) && (
                        <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                          Please enter a valid email address
                        </div>
                      )}
                  </Col>

                  <Col>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        setOtherEmail("");
                        setShowOtherInput(false);
                      }}
                    />
                  </Col>
                </Row>
              )}

            </>

          )}
        </div>
      </div>
      <Modal
        open={showSubmitSuccessModal}
        centered
        closable={false}
        footer={[
          <Button
            key="done"
            type="primary"
            onClick={() => {
              setShowSubmitSuccessModal(false);
              onSubmit();
            }}
          >
            Done
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", padding: "10px 20px" }}>
          <div
            style={{
              fontSize: 40,
              color: "#52c41a",
              marginBottom: 12,
            }}
          >
            ✓
          </div>

          <h3 style={{ marginBottom: 12 }}>
            Email Configuration Submitted
          </h3>

          <p style={{ color: "#676373", marginBottom: 16 }}>
            Your email configuration has been submitted successfully.
            A setup request has been sent to the IT/Development team
            for review and email template setup.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              borderRadius: 6,
              padding: "12px 16px",
              textAlign: "left",
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <strong>Status:</strong>{" "}
              <span style={{ color: "#d48806" }}>
                Pending Approval
              </span>
            </div>

            <div>
              <strong>Next Step:</strong>{" "}
              IT/Development team review and setup
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        open={showTestEmailModal}
        centered
        closable={false}
        onCancel={() => {
          setShowTestEmailModal(false);
          onSubmit();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowTestEmailModal(false);
              onSubmit();
            }}
          >
            Not Now
          </Button>,

          <Button
            key="send"
            type="primary"
            loading={isTestEmailLoading}
            disabled={isTestEmailLoading}
            onClick={handleSendTestEmail}

          >
            Send Test Email
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", padding: "10px 20px 5px" }}>
          <div
            style={{
              fontSize: 40,
              marginBottom: 12,
            }}
          >
            ✉️
          </div>

          <h3 style={{ marginBottom: 10 }}>
            Configuration Updated Successfully
          </h3>

          <p
            style={{
              color: "#676373",
              marginBottom: 20,
              lineHeight: 1.6,
            }}
          >
            Your email configuration has been saved successfully.
            Would you like to send a test email to verify the updated
            configuration?
          </p>
        </div>
      </Modal>
    </Drawer>

  );


};

export default CreateEditEmailConfigurationComponent;

